import { ArticleTeaserDefaultProps } from '@/components/ArticleTeaser/Default';
import { useAppState } from '@/hooks/useAppState';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeOptions } from '@/utils/merge';
import { withTrackingAttributes } from '@/utils/withTrackingAttributes';
import Tracking from 'base/components/TrackingComponent';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { ReactNode } from 'react';
import { RecommendedArticles } from './RecommendedArticles';

export interface RecommendedArticleProps extends ArticleTeaserDefaultProps {
  type?: string;
  model?: string;
}

export interface StandaloneRecommendedArticles extends StandaloneComponentProps<typeof RecommendedArticles> {
  articles?: RecommendedArticleProps[];
  articlesCount?: number;
  headline?: ReactNode;
  isLoading?: boolean;
}

const mockedSkeletonData = new Array(4).fill(null).map((_, i) => <RecommendedArticles.Skeleton key={i} />);

export const StandaloneRecommendedArticles: StandaloneComponent<StandaloneRecommendedArticles> = ({
  articles,
  articlesCount,
  headline,
  isLoading,
  ...props
}) => {
  const [{ accessLevel }] = useAppState();

  const { enabled, excludedAccessLevels } = getOcelotConfig('features.recommendedArticles');

  if (!enabled || excludedAccessLevels.includes(accessLevel!) || !articles?.length) return <></>;

  const mappedArticles = articles.slice(0, articlesCount ?? 4).map((article, index) => {
    const metaInfo = {
      model: article.model,
      recommendation_type: article.type,
    };

    return (
      <RecommendedArticles.ListItem
        key={index}
        {...withTrackingAttributes({
          category: 'recommendation_engine',
          name: `${article.headline}`,
          extraInfo: [metaInfo],
          url: `${article.links?.article?.href}`,
          placement: `${index + 1}`,
        })}
      >
        <RecommendedArticles.ArticleTeaser
          {...article}
          options={mergeOptions({ variant: 'vertical' }, props.options?.$articleTeaser, article.options)}
        />
      </RecommendedArticles.ListItem>
    );
  });

  const tiles = isLoading ? mockedSkeletonData : mappedArticles;

  return (
    <Tracking>
      <RecommendedArticles {...props}>
        <RecommendedArticles.Headline>{headline ?? 'Rekommenderad läsning'}</RecommendedArticles.Headline>
        <RecommendedArticles.List>{tiles}</RecommendedArticles.List>
      </RecommendedArticles>
    </Tracking>
  );
};

export type RecommendedArticlesData = Pick<
  ArticleTeaserDefaultProps,
  'caption' | 'headline' | 'description' | 'image' | 'links'
>[];
